import { Datagrid, FunctionField, ListContextProvider, ReferenceField, useRecordContext } from "react-admin";
import { useQuery } from "react-query";
import { DateField } from "../../fields/DateField";
import { calendarEventTitle } from "../../utils/calendar";
import { listContextFromArray } from "../../utils/listContextFromArray";
import {
  ASFCourseSession,
  AutovioCalendarEvent,
  CourseLesson,
  isTheoryLesson,
  TheoryLesson,
} from "../../model/autovioCalendarEvents";
import { InstructorField } from "../../fields/InstructorField";
import { UserDisplayName } from "../../misc/UserDisplayName";
import { autovioColors } from "../../misc/backofficeTheme";
import { cloneElement, ReactElement } from "react";
import { DateTime } from "luxon";
import { Course, CourseEvent } from "../../providers/coursesProvider";
import { courseParticipantsProvider } from "../../providers/courseParticipantsProvider";
import { LocationField } from "../../fields/LocationField";
import { l10n } from "../../l10n";
import { assertNever } from "../../backoffice.utils";

type AttendeeEvent =
  | Pick<TheoryLesson, "type" | "numBookedStudents" | "maxStudents">
  | Pick<ASFCourseSession, "type" | "participants">
  | Pick<CourseLesson, "type">;

export function CourseEventsList({ course, openEvent }: { course: Course; openEvent: (event: CourseEvent) => false }) {
  const { data: courseParticipants } = useQuery(
    ["courseParticipants", course.id],
    () => courseParticipantsProvider.getAllForCourse(course.id),
    { enabled: course.isBKFCourse },
  );

  return (
    <ListContextProvider value={listContextFromArray(course.events)}>
      <Datagrid bulkActionButtons={false} rowClick={(_, __, event) => openEvent(event as CourseEvent)}>
        <_StyleAppointmentWrapper label="Datum, Uhrzeit">
          <DateField source="start" showTime />
        </_StyleAppointmentWrapper>
        <_StyleAppointmentWrapper label="Art">
          <FunctionField render={_renderEventType} />
        </_StyleAppointmentWrapper>
        <_StyleAppointmentWrapper label="Teilnehmer">
          <FunctionField render={(event: CourseEvent) => _renderAttendees(event, courseParticipants?.length)} />
        </_StyleAppointmentWrapper>
        <_StyleAppointmentWrapper label={course.isBKFCourse ? "Dozent" : "Fahrlehrer"}>
          {course.isBKFCourse ? (
            <FunctionField
              render={(event: CourseLesson) =>
                event.instructorId ? (
                  <InstructorField source="instructorId" />
                ) : event.lecturerId ? (
                  <UserDisplayName userId={event.lecturerId} component="span" variant="body2" />
                ) : null
              }
            />
          ) : (
            <InstructorField source="instructorId" />
          )}
        </_StyleAppointmentWrapper>
        <_StyleAppointmentWrapper label={course.isBKFCourse ? "Raum" : "Ort"}>
          {course.isBKFCourse ? (
            <FunctionField
              render={(event: CourseLesson) =>
                event.roomId ? <ReferenceField record={event} reference="rooms" source="roomId" link={false} /> : null
              }
            />
          ) : (
            <LocationField />
          )}
        </_StyleAppointmentWrapper>
      </Datagrid>
    </ListContextProvider>
  );
}

function _StyleAppointmentWrapper({ children }: { children: ReactElement; label: string }) {
  const appointment = useRecordContext<AutovioCalendarEvent>();

  if (!appointment) {
    return null;
  }

  if (appointment.deleted) {
    return cloneElement(children, { sx: { color: autovioColors.red } });
  } else if (appointment.start < DateTime.now()) {
    return cloneElement(children, { sx: { color: "text.secondary" } });
  } else {
    return children;
  }
}

function _renderEventType(courseEvent: CourseEvent) {
  if (isTheoryLesson(courseEvent)) {
    return l10n.lectureXxx(courseEvent);
  } else {
    return calendarEventTitle(courseEvent);
  }
}

function _renderAttendees(courseEvent: AttendeeEvent, courseParticipantCount?: number): string {
  switch (courseEvent.type) {
    case "TheoryLesson":
      return `${courseEvent.numBookedStudents}/${courseEvent.maxStudents}`;
    case "ASFCourseSession":
      return Object.keys(courseEvent.participants).length.toString();
    case "CourseLesson":
      if (typeof courseParticipantCount !== "number") return "...";
      return courseParticipantCount === 0 ? "-" : courseParticipantCount.toString();
    default:
      return assertNever(courseEvent);
  }
}
